import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getIssues = async (token) => {
  const url = 'admin/issues';
  const data = await apiGet({ url, token });
  return data;
};

const getIssue = async (token, id) => {
  const url = `admin/issues/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createIssue = async (token, params) => {
  const url = 'admin/issues';
  const data = await apiPost({ url, token, objectName: 'issue', params });
  return data;
};

const updateIssue = async (token, id, params) => {
  const url = `admin/issues/${id}`;
  const data = await apiPatch({ url, token, objectName: 'issue', params });
  return data;
};

const deleteIssue = async (token, id) => {
  const url = `admin/issues/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminIssues({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getIssues(token);
  const queryKey = 'admin-issues';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminIssue({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getIssue(token, id);
  const queryKey = ['admin-issue', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminIssue({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createIssue(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-issues');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminIssue({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateIssue(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-issues');
        queryClient.invalidateQueries(['admin-issue', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminIssue({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteIssue(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-issues');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
